import Papa from 'papaparse';
import { RawMaterial, MenuItem } from '../services/dataService';

// CSV Column definitions and validation rules
export interface CSVColumn {
  key: string;
  label: string;
  required: boolean;
  type: 'string' | 'number' | 'boolean' | 'date' | 'array';
  description?: string;
}

export const RAW_MATERIAL_CSV_COLUMNS: CSVColumn[] = [
  { key: 'sku', label: 'SKU', required: false, type: 'string', description: 'Unique SKU identifier' },
  { key: 'name', label: 'Name', required: true, type: 'string', description: 'Material name (unique)' },
  { key: 'category', label: 'Category', required: true, type: 'string', description: 'Material category' },
  { key: 'baseUnit', label: 'Base Unit', required: true, type: 'string', description: 'Base unit (e.g., kg, liter, piece)' },
  { key: 'unitConversions', label: 'Unit Conversions', required: false, type: 'string', description: 'JSON string of unit conversions array' },
  { key: 'costPerUnit', label: 'Cost Per Unit', required: true, type: 'number', description: 'Cost per base unit' },
  { key: 'currentStock', label: 'Current Stock', required: true, type: 'number', description: 'Current stock quantity in base units' },
  { key: 'minStock', label: 'Min Stock', required: true, type: 'number', description: 'Minimum stock level in base units' },
  { key: 'maxStock', label: 'Max Stock', required: true, type: 'number', description: 'Maximum stock level in base units' },
  { key: 'active', label: 'Active', required: false, type: 'boolean', description: 'Whether material is active (true/false)' },
  { key: 'supplier', label: 'Supplier', required: false, type: 'string', description: 'Supplier name' },
  { key: 'supplierContact', label: 'Supplier Contact', required: false, type: 'string', description: 'Phone or email' },
  { key: 'location', label: 'Location', required: false, type: 'string', description: 'Storage location' },
  { key: 'description', label: 'Description', required: false, type: 'string', description: 'Material description' },
  { key: 'expiryDate', label: 'Expiry Date', required: false, type: 'date', description: 'Expiry date (YYYY-MM-DD)' }
];

export const MENU_ITEM_CSV_COLUMNS: CSVColumn[] = [
  { key: 'name', label: 'Name', required: true, type: 'string', description: 'Menu item name (unique)' },
  { key: 'category', label: 'Category', required: true, type: 'string', description: 'Menu category' },
  { key: 'price', label: 'Price', required: true, type: 'number', description: 'Item price' },
  { key: 'preparationTime', label: 'Preparation Time', required: true, type: 'number', description: 'Prep time in minutes' },
  { key: 'description', label: 'Description', required: false, type: 'string', description: 'Item description' },
  { key: 'isAvailable', label: 'Available', required: false, type: 'boolean', description: 'Available for ordering (true/false)' },
  { key: 'autoDeductInventory', label: 'Auto Deduct Inventory', required: false, type: 'boolean', description: 'Auto-deduct ingredients (true/false)' },
  { key: 'isFeatured', label: 'Featured', required: false, type: 'boolean', description: 'Featured item (true/false)' },
  { key: 'isSpecialOffer', label: 'Special Offer', required: false, type: 'boolean', description: 'Special offer (true/false)' },
  { key: 'isTodaysSpecial', label: "Today's Special", required: false, type: 'boolean', description: "Today's special (true/false)" },
  { key: 'originalPrice', label: 'Original Price', required: false, type: 'number', description: 'Original price (for special offers)' },
  { key: 'spiceLevel', label: 'Spice Level', required: false, type: 'number', description: 'Spice level 0-5' },
  { key: 'dietaryInfo', label: 'Dietary Info', required: false, type: 'array', description: 'Comma-separated dietary info' },
  { key: 'badges', label: 'Badges', required: false, type: 'array', description: 'Comma-separated custom badges' },
  { key: 'tags', label: 'Tags', required: false, type: 'array', description: 'Comma-separated tags' }
];

// Validation error interface
export interface ValidationError {
  row: number;
  field: string;
  message: string;
  value?: any;
}

// Import result interface
export interface ImportResult {
  success: boolean;
  created: number;
  updated: number;
  errors: ValidationError[];
  totalRows: number;
}

// CSV utility functions
export class CSVUtils {
  
  // Generate CSV template with headers and example data
  static generateTemplate(type: 'rawMaterial' | 'menuItem'): string {
    const columns = type === 'rawMaterial' ? RAW_MATERIAL_CSV_COLUMNS : MENU_ITEM_CSV_COLUMNS;
    
    // Create header row
    const headers = columns.map(col => col.label);
    
    // Create example row
    const exampleData = columns.map(col => {
      switch (col.type) {
        case 'string':
          return col.key === 'sku' ? 'COFFEE-001' :
                 col.key === 'name' ? 'Coffee Beans - Arabica' : 
                 col.key === 'category' ? 'Beverages' :
                 col.key === 'baseUnit' ? 'kg' :
                 col.key === 'unitConversions' ? '[{"unitName":"bag","conversionFactor":5,"isPurchaseUnit":true,"isSaleUnit":false,"costPerConversionUnit":25.00}]' :
                 col.key === 'supplier' ? 'Coffee Supplier Co.' :
                 col.key === 'description' ? 'High quality coffee beans' :
                 col.key === 'location' ? 'Warehouse A, Shelf 3' :
                 col.key === 'supplierContact' ? 'contact@supplier.com' : 'Example Value';
        case 'number':
          return col.key === 'currentStock' ? '50' :
                 col.key === 'minStock' ? '10' :
                 col.key === 'maxStock' ? '100' :
                 col.key === 'costPerUnit' ? '5.00' :
                 col.key === 'price' ? '4.50' :
                 col.key === 'preparationTime' ? '5' :
                 col.key === 'originalPrice' ? '5.00' :
                 col.key === 'spiceLevel' ? '0' : '0';
        case 'boolean':
          return 'true';
        case 'date':
          return '2024-12-31';
        case 'array':
          return col.key === 'dietaryInfo' ? 'Vegetarian,Organic' :
                 col.key === 'badges' ? 'Chef Special,New' :
                 col.key === 'tags' ? 'coffee,premium' : 'item1,item2';
        default:
          return '';
      }
    });
    
    // Combine headers and example
    const csvContent = [headers, exampleData].map(row => 
      row.map(cell => this.escapeCSVField(cell)).join(',')
    ).join('\n');
    
    // Add UTF-8 BOM for Excel compatibility
    return '\uFEFF' + csvContent;
  }
  
  // Parse CSV file
  static parseCSV(file: File): Promise<{ data: any[], errors: any[] }> {
    return new Promise((resolve) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => {
          // Convert header back to key format
          return RAW_MATERIAL_CSV_COLUMNS.concat(MENU_ITEM_CSV_COLUMNS)
            .find(col => col.label === header)?.key || header;
        },
        complete: (results) => {
          resolve({
            data: results.data,
            errors: results.errors
          });
        }
      });
    });
  }
  
  // Validate raw material data
  static validateRawMaterial(data: any, rowIndex: number): ValidationError[] {
    const errors: ValidationError[] = [];
    const columns = RAW_MATERIAL_CSV_COLUMNS;
    
    // Check required fields
    columns.forEach(col => {
      if (col.required && (!data[col.key] || data[col.key] === '')) {
        errors.push({
          row: rowIndex + 1,
          field: col.label,
          message: `${col.label} is required`,
          value: data[col.key]
        });
      }
    });
    
    // Validate that either sku or name is provided
    if (!data.sku && !data.name) {
      errors.push({
        row: rowIndex + 1,
        field: 'SKU/Name',
        message: 'Either SKU or Name must be provided',
        value: ''
      });
    }
    
    // Validate numeric fields
    const numericFields = ['currentStock', 'minStock', 'maxStock', 'costPerUnit'];
    numericFields.forEach(field => {
      if (data[field] !== undefined && data[field] !== '') {
        const num = parseFloat(data[field]);
        if (isNaN(num)) {
          errors.push({
            row: rowIndex + 1,
            field: columns.find(c => c.key === field)?.label || field,
            message: 'Must be a valid number',
            value: data[field]
          });
        } else if (num < 0) {
          errors.push({
            row: rowIndex + 1,
            field: columns.find(c => c.key === field)?.label || field,
            message: 'Must be greater than or equal to 0',
            value: data[field]
          });
        }
      }
    });
    
    // Validate stock levels
    if (data.minStock && data.maxStock) {
      const minStock = parseFloat(data.minStock);
      const maxStock = parseFloat(data.maxStock);
      if (!isNaN(minStock) && !isNaN(maxStock) && minStock >= maxStock) {
        errors.push({
          row: rowIndex + 1,
          field: 'Stock Levels',
          message: 'Minimum stock must be less than maximum stock',
          value: `${data.minStock} >= ${data.maxStock}`
        });
      }
    }
    
    // Validate unit conversions JSON
    if (data.unitConversions && data.unitConversions !== '') {
      try {
        const conversions = JSON.parse(data.unitConversions);
        if (!Array.isArray(conversions)) {
          errors.push({
            row: rowIndex + 1,
            field: 'Unit Conversions',
            message: 'Must be a valid JSON array',
            value: data.unitConversions
          });
        }
      } catch (e) {
        errors.push({
          row: rowIndex + 1,
          field: 'Unit Conversions',
          message: 'Must be a valid JSON string',
          value: data.unitConversions
        });
      }
    }
    
    // Validate boolean fields
    if (data.active !== undefined && data.active !== '') {
      const value = data.active.toLowerCase();
      if (!['true', 'false', '1', '0', 'yes', 'no'].includes(value)) {
        errors.push({
          row: rowIndex + 1,
          field: 'Active',
          message: 'Must be true/false, 1/0, or yes/no',
          value: data.active
        });
      }
    }
    
    // Validate date
    if (data.expiryDate && data.expiryDate !== '') {
      const date = new Date(data.expiryDate);
      if (isNaN(date.getTime())) {
        errors.push({
          row: rowIndex + 1,
          field: 'Expiry Date',
          message: 'Must be a valid date (YYYY-MM-DD)',
          value: data.expiryDate
        });
      }
    }
    
    return errors;
  }
  
  // Validate menu item data
  static validateMenuItem(data: any, rowIndex: number): ValidationError[] {
    const errors: ValidationError[] = [];
    const columns = MENU_ITEM_CSV_COLUMNS;
    
    // Check required fields
    columns.forEach(col => {
      if (col.required && (!data[col.key] || data[col.key] === '')) {
        errors.push({
          row: rowIndex + 1,
          field: col.label,
          message: `${col.label} is required`,
          value: data[col.key]
        });
      }
    });
    
    // Validate numeric fields
    const numericFields = ['price', 'preparationTime', 'originalPrice', 'spiceLevel'];
    numericFields.forEach(field => {
      if (data[field] !== undefined && data[field] !== '') {
        const num = parseFloat(data[field]);
        if (isNaN(num)) {
          errors.push({
            row: rowIndex + 1,
            field: columns.find(c => c.key === field)?.label || field,
            message: 'Must be a valid number',
            value: data[field]
          });
        } else if (num < 0) {
          errors.push({
            row: rowIndex + 1,
            field: columns.find(c => c.key === field)?.label || field,
            message: 'Must be greater than or equal to 0',
            value: data[field]
          });
        }
      }
    });
    
    // Validate price
    if (data.price) {
      const price = parseFloat(data.price);
      if (!isNaN(price) && price <= 0) {
        errors.push({
          row: rowIndex + 1,
          field: 'Price',
          message: 'Must be greater than 0',
          value: data.price
        });
      }
    }
    
    // Validate preparation time
    if (data.preparationTime) {
      const time = parseFloat(data.preparationTime);
      if (!isNaN(time) && time <= 0) {
        errors.push({
          row: rowIndex + 1,
          field: 'Preparation Time',
          message: 'Must be greater than 0',
          value: data.preparationTime
        });
      }
    }
    
    // Validate spice level
    if (data.spiceLevel) {
      const spiceLevel = parseInt(data.spiceLevel);
      if (!isNaN(spiceLevel) && (spiceLevel < 0 || spiceLevel > 5)) {
        errors.push({
          row: rowIndex + 1,
          field: 'Spice Level',
          message: 'Must be between 0 and 5',
          value: data.spiceLevel
        });
      }
    }
    
    // Validate boolean fields
    const booleanFields = ['isAvailable', 'autoDeductInventory', 'isFeatured', 'isSpecialOffer', 'isTodaysSpecial'];
    booleanFields.forEach(field => {
      if (data[field] !== undefined && data[field] !== '') {
        const value = data[field].toLowerCase();
        if (!['true', 'false', '1', '0', 'yes', 'no'].includes(value)) {
          errors.push({
            row: rowIndex + 1,
            field: columns.find(c => c.key === field)?.label || field,
            message: 'Must be true/false, 1/0, or yes/no',
            value: data[field]
          });
        }
      }
    });
    
    return errors;
  }
  
  // Transform CSV data to RawMaterial object
  static csvToRawMaterial(csvData: any): Partial<RawMaterial> {
    const data = { ...csvData };
    
    // Convert numeric fields
    const numericFields = ['currentStock', 'minStock', 'maxStock', 'costPerUnit'];
    numericFields.forEach(field => {
      if (data[field] !== undefined && data[field] !== '') {
        data[field] = parseFloat(data[field]);
      }
    });
    
    // Convert boolean fields
    if (data.active !== undefined && data.active !== '') {
      const value = data.active.toLowerCase();
      data.active = ['true', '1', 'yes'].includes(value);
    } else {
      data.active = true; // Default to active
    }
    
    // Convert date
    if (data.expiryDate && data.expiryDate !== '') {
      data.expiryDate = new Date(data.expiryDate);
    } else {
      delete data.expiryDate;
    }
    
    // Parse unit conversions JSON
    if (data.unitConversions && data.unitConversions !== '') {
      try {
        data.unitConversions = JSON.parse(data.unitConversions);
      } catch (e) {
        data.unitConversions = [];
      }
    } else {
      data.unitConversions = [];
    }
    
    // Set default values for legacy compatibility
    data.baseUnit = data.baseUnit || 'piece';
    data.costPerUnit = data.costPerUnit || 0;
    data.defaultPurchaseUnit = data.baseUnit;
    data.defaultSaleUnit = data.baseUnit;
    
    // Remove empty optional fields
    const optionalFields = ['supplierContact', 'location', 'description', 'supplier'];
    optionalFields.forEach(field => {
      if (!data[field] || data[field] === '') {
        delete data[field];
      }
    });
    
    return data;
  }
  
  // Transform CSV data to MenuItem object
  static csvToMenuItem(csvData: any): Partial<MenuItem> {
    const data = { ...csvData };
    
    // Convert numeric fields
    const numericFields = ['price', 'preparationTime', 'originalPrice', 'spiceLevel'];
    numericFields.forEach(field => {
      if (data[field] !== undefined && data[field] !== '') {
        data[field] = parseFloat(data[field]);
      }
    });
    
    // Convert boolean fields
    const booleanFields = ['isAvailable', 'autoDeductInventory', 'isFeatured', 'isSpecialOffer', 'isTodaysSpecial'];
    booleanFields.forEach(field => {
      if (data[field] !== undefined && data[field] !== '') {
        const value = data[field].toLowerCase();
        data[field] = ['true', '1', 'yes'].includes(value);
      } else {
        // Set default values
        data[field] = field === 'isAvailable' ? true : false;
      }
    });
    
    // Convert array fields
    const arrayFields = ['dietaryInfo', 'badges', 'tags'];
    arrayFields.forEach(field => {
      if (data[field] && data[field] !== '') {
        data[field] = data[field].split(',').map((item: string) => item.trim()).filter((item: string) => item);
      } else {
        delete data[field];
      }
    });
    
    // Remove empty optional fields
    const optionalFields = ['description', 'originalPrice', 'spiceLevel'];
    optionalFields.forEach(field => {
      if (!data[field] || data[field] === '') {
        delete data[field];
      }
    });
    
    // Set default values
    data.ingredients = []; // Will be added manually
    data.saleUnit = 'piece';
    data.saleUnitConversion = 1;
    
    return data;
  }
  
  // Export data to CSV
  static exportToCSV(data: any[], type: 'rawMaterial' | 'menuItem'): string {
    const columns = type === 'rawMaterial' ? RAW_MATERIAL_CSV_COLUMNS : MENU_ITEM_CSV_COLUMNS;
    
    // Create header row
    const headers = columns.map(col => col.label);
    
    // Transform data to CSV format
    const rows = data.map(item => {
      return columns.map(col => {
        let value = item[col.key];
        
        // Handle different data types
        switch (col.type) {
          case 'boolean':
            return value ? 'true' : 'false';
          case 'date':
            return value ? new Date(value).toISOString().split('T')[0] : '';
          case 'array':
            return Array.isArray(value) ? value.join(',') : '';
          default:
            // Special handling for unitConversions - serialize as JSON string
            if (col.key === 'unitConversions' && Array.isArray(value)) {
              return JSON.stringify(value);
            }
            return value || '';
        }
      });
    });
    
    // Combine headers and data
    const csvContent = [headers, ...rows].map(row => 
      row.map(cell => this.escapeCSVField(cell)).join(',')
    ).join('\n');
    
    // Add UTF-8 BOM for Excel compatibility
    return '\uFEFF' + csvContent;
  }
  
  // Escape CSV field
  private static escapeCSVField(field: any): string {
    if (field === null || field === undefined) {
      return '';
    }
    
    const str = String(field);
    
    // If field contains comma, quote, or newline, wrap in quotes and escape quotes
    if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
      return '"' + str.replace(/"/g, '""') + '"';
    }
    
    return str;
  }
  
  // Download CSV file
  static downloadCSV(content: string, filename: string): void {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
  
  // Generate error report CSV
  static generateErrorReport(errors: ValidationError[]): string {
    const headers = ['Row', 'Field', 'Message', 'Value'];
    const rows = errors.map(error => [
      error.row,
      error.field,
      error.message,
      error.value || ''
    ]);
    
    const csvContent = [headers, ...rows].map(row => 
      row.map(cell => this.escapeCSVField(cell)).join(',')
    ).join('\n');
    
    return '\uFEFF' + csvContent;
  }
}
